import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Header } from "@/components/admin/BookingsTab";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Shield, ShieldCheck, KeyRound, Save } from "lucide-react";

const PRETTY = {
  "bookings.read": "View bookings",
  "bookings.write": "Assign / update bookings",
  "drivers.read": "View drivers",
  "drivers.write": "Manage drivers",
  "applications.review": "Review driver applications",
  "services.write": "Manage services",
  "payments.write": "Manage payment methods",
  "promos.write": "Manage promo codes",
  "business.write": "Manage corporate accounts",
  "reports.read": "View reports",
  "audit.read": "View audit logs",
  "settings.write": "Edit settings",
};

const PRESET_TONE = {
  dispatch: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20",
  finance:  "bg-amber-400/10 border-amber-400/30 text-amber-300 hover:bg-amber-400/20",
  fleet:    "bg-amber-400/10 border-amber-400/30 text-amber-300 hover:bg-amber-400/20",
  full:     "bg-red-500/10 border-red-500/30 text-red-300 hover:bg-red-500/20",
};
const PRESET_HINT = {
  dispatch: "Low risk — read-only for reports",
  finance:  "Medium risk — corporate & audit",
  fleet:    "Medium risk — driver & fleet control",
  full:     "High risk — everything except Settings",
};

const GROUPS = [
  { name: "Bookings & Dispatch", perms: ["bookings.read", "bookings.write"] },
  { name: "Fleet", perms: ["drivers.read", "drivers.write", "applications.review"] },
  { name: "Services & Payments", perms: ["services.write", "payments.write", "promos.write"] },
  { name: "Business", perms: ["business.write"] },
  { name: "Reports & Audit", perms: ["reports.read", "audit.read"] },
  { name: "Settings", perms: ["settings.write"] },
];

export default function StaffTab({ currentUserId }) {
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState({ keys: [], presets: {} });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "staff", preset: "dispatch" });
  const [permsFor, setPermsFor] = useState(null); // user object under edit
  const [permsDraft, setPermsDraft] = useState([]);
  const [impact, setImpact] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/users"); setUsers(data); }
    catch { toast.error("Load failed"); }
  };
  const loadMeta = async () => {
    try { const { data } = await api.get("/users/permissions/meta"); setMeta(data); }
    catch { /* silent */ }
  };
  useEffect(() => { load(); loadMeta(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      const perms = form.role === "staff" ? (meta.presets[form.preset] || []) : [];
      await api.post("/users", { email: form.email, password: form.password, name: form.name, role: form.role, permissions: perms });
      toast.success("User created"); setOpen(false);
      setForm({ email: "", password: "", name: "", role: "staff", preset: "dispatch" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleActive = async (u) => {
    try { await api.patch(`/users/${u.id}`, { active: !u.active }); load(); }
    catch { toast.error("Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openPerms = async (u) => {
    if (u.role === "super_admin") { toast.info("Super admins already have every permission"); return; }
    setPermsFor(u);
    setPermsDraft(u.permissions || []);
    try {
      const { data } = await api.get(`/users/${u.id}/permission-impact`);
      setImpact(data.impacts || {});
    } catch { setImpact({}); }
  };
  const togglePerm = (k) => setPermsDraft((d) => (d.includes(k) ? d.filter((p) => p !== k) : [...d, k]));
  const applyPreset = (name) => setPermsDraft(meta.presets[name] || []);
  const savePerms = async () => {
    // Compare and warn on downgrade
    const before = new Set(permsFor.permissions || []);
    const after = new Set(permsDraft);
    const removed = [...before].filter((p) => !after.has(p));
    if (removed.length) {
      const lines = removed.map((p) => {
        const imp = impact[p];
        return `• ${PRETTY[p] || p}${imp ? ` — will lose visibility of ${imp.count} ${imp.label.toLowerCase()}` : ""}`;
      }).join("\n");
      if (!window.confirm(
        `You're removing ${removed.length} permission${removed.length > 1 ? "s" : ""} from ${permsFor.name}.\n\n${lines}\n\nProceed?`
      )) return;
    }
    setSaving(true);
    try {
      await api.patch(`/users/${permsFor.id}/permissions`, { permissions: permsDraft });
      toast.success("Permissions saved");
      setPermsFor(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <Header title="Staff & Admins" subtitle="Role-based access with granular permissions per sub-admin">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="btn-gold rounded-full" data-testid="staff-add"><Plus className="w-4 h-4 mr-2" /> Add user</Button></DialogTrigger>
          <DialogContent className="bg-[#111827] border-white/10 text-white">
            <DialogHeader><DialogTitle className="font-display">Create staff account</DialogTitle></DialogHeader>
            <form onSubmit={add} className="grid gap-4">
              <div><Label className="text-xs uppercase text-slate-400">Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-black/30 border-white/10 mt-1 h-10" data-testid="staff-form-name" /></div>
              <div><Label className="text-xs uppercase text-slate-400">Email</Label><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-black/30 border-white/10 mt-1 h-10" data-testid="staff-form-email" /></div>
              <div><Label className="text-xs uppercase text-slate-400">Password</Label><Input required type="password" minLength="6" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-black/30 border-white/10 mt-1 h-10" data-testid="staff-form-password" /></div>
              <div><Label className="text-xs uppercase text-slate-400">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="bg-black/30 border-white/10 mt-1 h-10" data-testid="staff-form-role"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#111827] border-white/10 text-white">
                    <SelectItem value="staff">Staff (sub-admin)</SelectItem>
                    <SelectItem value="super_admin">Super Admin (full access)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.role === "staff" && (
                <div>
                  <Label className="text-xs uppercase text-slate-400">Preset</Label>
                  <Select value={form.preset} onValueChange={(v) => setForm({ ...form, preset: v })}>
                    <SelectTrigger className="bg-black/30 border-white/10 mt-1 h-10" data-testid="staff-form-preset"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#111827] border-white/10 text-white">
                      <SelectItem value="dispatch">Dispatch (bookings + drivers view + reports)</SelectItem>
                      <SelectItem value="finance">Finance (bookings + reports + corporate + audit)</SelectItem>
                      <SelectItem value="fleet">Fleet (drivers + applications + services)</SelectItem>
                      <SelectItem value="full">Full (everything except Settings)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-slate-500 mt-1.5">Fine-tune later using the <b>Permissions</b> button on the user row.</p>
                </div>
              )}
              <Button type="submit" className="btn-gold" data-testid="staff-form-submit">Create</Button>
            </form>
          </DialogContent>
        </Dialog>
      </Header>

      <div className="glass-card rounded-2xl border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.03] text-xs uppercase tracking-wider text-slate-400">
            <tr>{["Name", "Email", "Role", "Permissions", "Active", "Actions"].map((h) => <th key={h} className="text-left px-4 py-3 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-white/5" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 font-medium">{u.name}</td>
                <td className="px-4 py-3 text-slate-300 font-mono text-xs">{u.email}</td>
                <td className="px-4 py-3">
                  <Badge className={`${u.role === "super_admin" ? "bg-amber-400/20 text-amber-300 border-amber-400/40" : "bg-slate-400/20 text-slate-300 border-slate-400/40"} border font-mono uppercase text-[10px] tracking-wider`}>{u.role.replace("_", " ")}</Badge>
                </td>
                <td className="px-4 py-3 text-slate-400 text-xs font-mono">
                  {u.role === "super_admin"
                    ? <span className="text-amber-400">all</span>
                    : (u.permissions && u.permissions.length ? `${u.permissions.length} of ${meta.keys.length}` : <span className="text-red-400">none</span>)}
                </td>
                <td className="px-4 py-3"><Switch checked={u.active} onCheckedChange={() => toggleActive(u)} disabled={u.id === currentUserId} data-testid={`user-active-${u.id}`} /></td>
                <td className="px-4 py-3">
                  <div className="flex gap-1.5">
                    {u.role !== "super_admin" && (
                      <Button variant="outline" size="sm" onClick={() => openPerms(u)} className="bg-transparent border-white/10 text-slate-300 h-8" data-testid={`user-perms-${u.id}`}>
                        <KeyRound className="w-3.5 h-3.5 mr-1.5" /> Permissions
                      </Button>
                    )}
                    {u.id !== currentUserId && (
                      <Button variant="outline" size="icon" onClick={() => del(u.id)} className="bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10 h-8 w-8" data-testid={`user-delete-${u.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Permissions editor dialog */}
      <Dialog open={!!permsFor} onOpenChange={(v) => { if (!v) setPermsFor(null); }}>
        <DialogContent className="bg-[#111827] border-white/10 text-white max-w-lg max-h-[85vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-amber-400" /> Permissions — {permsFor?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase text-slate-400">Apply preset</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {Object.keys(meta.presets).map((p) => (
                  <button
                    key={p}
                    onClick={() => applyPreset(p)}
                    title={PRESET_HINT[p] || ""}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-widest border ${PRESET_TONE[p] || "bg-black/40 border-white/10 text-slate-300 hover:bg-amber-400/10"}`}
                    data-testid={`perms-preset-${p}`}
                  >
                    {p}
                  </button>
                ))}
                <button onClick={() => setPermsDraft([])} className="px-3 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-widest bg-black/40 border border-red-500/20 text-red-400 hover:bg-red-500/10" data-testid="perms-preset-none">clear</button>
              </div>
              {permsDraft.length > 0 && (
                <div className="text-[11px] text-slate-500 mt-2 font-mono">
                  {permsDraft.length} of {meta.keys.length} permissions selected
                </div>
              )}
            </div>

            {GROUPS.map((g) => (
              <div key={g.name} className="bg-black/20 border border-white/5 rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2">{g.name}</div>
                <div className="space-y-2">
                  {g.perms.filter((p) => meta.keys.includes(p)).map((k) => (
                    <label key={k} className="flex items-center gap-3 text-sm cursor-pointer">
                      <Checkbox checked={permsDraft.includes(k)} onCheckedChange={() => togglePerm(k)} className="border-white/20" data-testid={`perm-${k}`} />
                      <span className="text-slate-200 flex-1">{PRETTY[k] || k}</span>
                      <span className="text-[10px] text-slate-500 font-mono">{k}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" className="bg-transparent border-white/10 text-slate-300" onClick={() => setPermsFor(null)}>Cancel</Button>
              <Button onClick={savePerms} disabled={saving} className="btn-gold" data-testid="perms-save"><Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save permissions"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
